import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { fmtDate, fmtDateTime, fieldLabel, money, prettyUser, type DonorfyPaired, type MemberGridRow, type MembershipGridRow, type MemberPayload, type MembershipPayload, type PaymentRow, type PaymentsPayload, type Pairing, type Diff, type DeclRow } from './api'
import { MasterSelector, PairingBox, type SystemName } from './pairing'
import { BatchesPanel, EditMemberDialog, PendingBadges, ResolveCell } from './changes'
import { Badge, Dl, MasterBadge, MemberLink, MembershipLink, Placeholder, StatusBadge, SyncBadge, boolVal, dateVal, useJson, val } from './common'
import { HistoryTable } from './integrations'

// The full-width record pages: a member (Details · Membership · Joint members · Payments ·
// Integrations) and a membership (Details · Members · Payments · Integrations).

type MemberTab = 'details' | 'membership' | 'joint' | 'payments' | 'integrations'
type MembershipTab = 'details' | 'members' | 'payments' | 'integrations'

function SubPills<T extends string>({ tabs, value, onChange }: { tabs: { key: T; label: string; n?: number | null }[]; value: T; onChange: (t: T) => void }) {
  return (
    <div className="mbr-pills" role="tablist">
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={t.key === value} className={'mbr-pill mbr-pill--sub' + (t.key === value ? ' active' : '')} onClick={() => onChange(t.key)}>
          {t.label}{t.n !== undefined && t.n !== null && <span className="mbr-pill-n">{t.n.toLocaleString('en-GB')}</span>}
        </button>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
export function MemberRecordPage() {
  const { id } = useParams()
  const memberId = Number(id)
  const { data, loading, error, reload } = useJson<MemberPayload>(Number.isInteger(memberId) && memberId > 0 ? `/api/membership/members/${memberId}` : null)
  const [tab, setTab] = useState<MemberTab>('details')
  const [editing, setEditing] = useState(false)

  if (loading && !data) return <div className="mbr-record"><p className="mbr-note">Loading the member…</p></div>
  if (error) return <div className="mbr-record"><p className="mbr-error">{error}</p><Link className="mbr-link mbr-back" to="/membership">Back to Members</Link></div>
  if (!data) return null
  const m = data.member
  const ms = data.membership
  const name = [val(m, 'title'), val(m, 'first_name'), val(m, 'surname')].filter(Boolean).join(' ') || val(m, 'full_name') || `Member ${m.member_id}`

  return (
    <div className="mbr-record">
      <Link className="mbr-link mbr-back" to="/membership">← Members</Link>
      <div className="mbr-record-head">
        <div>
          <h2>{name}</h2>
          <div className="mbr-record-sub">
            {m.membership_card_number && <span>Card <strong>{m.membership_card_number}</strong></span>}
            {ms && <span>Membership <MembershipLink id={ms.membership_id}><strong>{ms.membership_number ?? ms.membership_id}</strong></MembershipLink> · {ms.category}{ms.type ? ` · ${ms.type}` : ''}</span>}
            {val(m, 'known_as') && <span>Known as {val(m, 'known_as')}</span>}
            {val(m, 'email') && <span>{val(m, 'email')}</span>}
            {(val(m, 'telephone') || val(m, 'mobile')) && <span>{[val(m, 'telephone'), val(m, 'mobile')].filter(Boolean).join(' · ')}</span>}
          </div>
        </div>
        <div className="mbr-chips">
          <StatusBadge status={m.status ?? null} />
          {m.life_member ? <Badge kind="info">Life member</Badge> : null}
          <SyncBadge status={m.sync_status} discrepancies={m.discrepancies} />
          <MasterBadge master={m.master} />
          <button type="button" className="btn-secondary" onClick={reload}>Reload</button>
        </div>
      </div>

      <SubPills<MemberTab> value={tab} onChange={setTab} tabs={[
        { key: 'details', label: 'Details' },
        { key: 'membership', label: 'Membership' },
        { key: 'joint', label: 'Joint members', n: data.joint_members.length },
        { key: 'payments', label: 'Payments', n: data.payments?.n ?? 0 },
        { key: 'integrations', label: 'Integrations', n: data.ontrack_differences.length || undefined },
      ]} />

      {tab === 'details' && <><div className="mbr-toolbar"><span className="mbr-note">Shared fields (names, contact, address, consents) can be edited here; everything else mirrors Ontrack.</span><span className="mbr-spacer" /><button type="button" className="btn-primary" onClick={() => setEditing(true)}>Edit details</button></div><MemberDetails m={m} />{editing && <EditMemberDialog member={m} onClose={() => setEditing(false)} onSaved={reload} />}</>}
      {tab === 'membership' && (
        ms ? <MembershipCard ms={ms} /> : <Placeholder title="No membership">this member is not linked to a membership in Ontrack.</Placeholder>
      )}
      {tab === 'joint' && <PeopleTable rows={data.joint_members} empty="No other members on this membership." />}
      {tab === 'payments' && <PaymentsPanel url={`/api/membership/members/${m.member_id}/payments`} giftAidApproved={ms?.gift_aid ?? null} declarations={data.donorfy_declarations ?? []} paired={Boolean(data.donorfy)} />}
      {tab === 'integrations' && <IntegrationsPanel entityType="member" entityId={m.member_id} pairings={data.pairings} master={m.master} diffs={data.ontrack_differences} historyCount={data.history_count} donorfy={data.donorfy ?? null} onChanged={reload} />}
    </div>
  )
}

function MemberDetails({ m }: { m: Record<string, unknown> }) {
  return (
    <div className="mbr-cols">
      <div className="mbr-section">
        <h3>Identity</h3>
        <Dl items={[['Title', val(m, 'title')], ['First name', val(m, 'first_name')], ['Middle name', val(m, 'middle_name')], ['Surname', val(m, 'surname')], ['Known as', val(m, 'known_as')], ['Full name', val(m, 'full_name')], ['Date of birth', dateVal(m, 'date_of_birth')], ['Gender code', val(m, 'gender_code')]]} />
        <h4>Contact</h4>
        <Dl items={[['Email', val(m, 'email')], ['Second email', val(m, 'email2')], ['Telephone', val(m, 'telephone')], ['Mobile', val(m, 'mobile')], ['Personal mobile', val(m, 'personal_mobile')], ['Personal email', val(m, 'personal_email')]]} />
      </div>
      <div className="mbr-section">
        <h3>Address</h3>
        <Dl items={[['Line 1', val(m, 'address_line1')], ['Line 2', val(m, 'address_line2')], ['Line 3', val(m, 'address_line3')], ['Line 4', val(m, 'address_line4')], ['Town', val(m, 'town')], ['Region', val(m, 'region')], ['County', val(m, 'county')], ['Postcode', val(m, 'postcode')], ['Country', val(m, 'country')]]} />
        <h4>Consents</h4>
        <Dl items={[['By post', boolVal(m, 'contact_by_post')], ['By phone', boolVal(m, 'contact_by_phone')], ['By email', boolVal(m, 'contact_by_email')], ['By SMS', boolVal(m, 'contact_by_sms')], ['For all', boolVal(m, 'contact_for_all')], ['For the Trust', boolVal(m, 'contact_for_trust')], ['For fundraising', boolVal(m, 'contact_for_fundraising')], ['For events', boolVal(m, 'contact_for_events')], ['For education', boolVal(m, 'contact_for_education')], ['For volunteering', boolVal(m, 'contact_for_volunteering')]]} />
      </div>
      <div className="mbr-section">
        <h3>Membership card</h3>
        <Dl items={[['Card number', val(m, 'membership_card_number')], ['Barcode', val(m, 'membership_barcode')], ['Life member', boolVal(m, 'life_member')], ['Premier life no', val(m, 'premier_life_number')], ['Premier first life no', val(m, 'premier_first_life_number')], ['AGM preference', val(m, 'agm_preference')], ['Description', val(m, 'description')]]} />
        <h4>Flags</h4>
        <Dl items={[['Gone away', boolVal(m, 'gone_away')], ['Deceased', boolVal(m, 'deceased')], ['Archived', boolVal(m, 'archived')], ['Archived on', dateVal(m, 'archived_date')], ['Employee', boolVal(m, 'is_employee')], ['Volunteer', boolVal(m, 'is_volunteer')], ['GoCardless customer', val(m, 'gocardless_customer_id')]]} />
        <h4>Record</h4>
        <Dl items={[['Ontrack member OID', val(m, 'ontrack_oid')], ['Ontrack person number', val(m, 'person_number')], ['Created in Ontrack', fmtDateTime(m.ontrack_created_on as string | null)], ['Last seen in Ontrack', fmtDateTime(m.ontrack_seen_at as string | null)], ['Deleted in Ontrack', fmtDateTime(m.ontrack_deleted_at as string | null)], ['Sync master', val(m, 'master') || 'none'], ['Sync status', val(m, 'sync_status')], ['Created here', `${fmtDateTime(m.created_at as string | null)} ${prettyUser(m.created_by as string | null)}`.trim()], ['Updated here', m.updated_at ? `${fmtDateTime(m.updated_at as string | null)} ${prettyUser(m.updated_by as string | null)}`.trim() : '']]} />
      </div>
    </div>
  )
}

function MembershipCard({ ms: row }: { ms: MembershipGridRow }) {
  const ms = row as unknown as Record<string, unknown> & { membership_id: number; has_dd: number; fee_paid: number | null }
  return (
    <div className="mbr-section">
      <h3><MembershipLink id={ms.membership_id}>Membership {val(ms, 'membership_number')}</MembershipLink> <span className="mbr-note">— open the membership record for every field and its members</span></h3>
      <div className="mbr-cols">
        <Dl items={[['Category', val(ms, 'category')], ['Type', val(ms, 'type')], ['Class', val(ms, 'class')], ['Status', <StatusBadge status={val(ms, 'status') || null} />], ['Members', val(ms, 'members_names')], ['People', val(ms, 'member_count')]]} />
        <Dl items={[['Start date', dateVal(ms, 'start_date')], ['Renewal date', dateVal(ms, 'renewal_date')], ['Last renewed', dateVal(ms, 'last_renewed')], ['Payment type', val(ms, 'payment_type')], ['Payment method', val(ms, 'payment_method')], ['Direct debit', ms.has_dd ? 'Yes' : 'No'], ['Gift Aid', boolVal(ms, 'gift_aid')], ['Fee paid', money(ms.fee_paid as number | null)]]} />
      </div>
    </div>
  )
}

function PeopleTable({ rows: people, empty }: { rows: MemberGridRow[]; empty: string }) {
  const rows = people as unknown as (Record<string, unknown> & { member_id: number })[]
  if (!rows.length) return <Placeholder title="Nobody else">{empty}</Placeholder>
  return (
    <div className="mbr-table-wrap">
      <table className="hc-table">
        <thead><tr><th>Name</th><th>Card no</th><th>Email</th><th>Telephone</th><th>Postcode</th><th>Status</th><th>Sync</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.member_id}>
              <td><MemberLink id={r.member_id}>{[val(r, 'title'), val(r, 'first_name'), val(r, 'surname')].filter(Boolean).join(' ') || val(r, 'full_name')}</MemberLink></td>
              <td>{val(r, 'membership_card_number')}</td>
              <td className="wrap">{val(r, 'email')}</td>
              <td>{val(r, 'telephone') || val(r, 'mobile')}</td>
              <td>{val(r, 'postcode')}</td>
              <td><StatusBadge status={val(r, 'status') || null} /></td>
              <td><SyncBadge status={val(r, 'sync_status') || null} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
export function MembershipRecordPage() {
  const { id } = useParams()
  const membershipId = Number(id)
  const { data, loading, error, reload } = useJson<MembershipPayload>(Number.isInteger(membershipId) && membershipId > 0 ? `/api/membership/memberships/${membershipId}` : null)
  const [tab, setTab] = useState<MembershipTab>('details')

  if (loading && !data) return <div className="mbr-record"><p className="mbr-note">Loading the membership…</p></div>
  if (error) return <div className="mbr-record"><p className="mbr-error">{error}</p><Link className="mbr-link mbr-back" to="/membership/memberships">Back to Memberships</Link></div>
  if (!data) return null
  const ms = data.membership

  return (
    <div className="mbr-record">
      <Link className="mbr-link mbr-back" to="/membership/memberships">← Memberships</Link>
      <div className="mbr-record-head">
        <div>
          <h2>Membership {ms.membership_number ?? ms.membership_id}</h2>
          <div className="mbr-record-sub">
            <span><strong>{ms.category}</strong>{val(ms, 'type') ? ` · ${val(ms, 'type')}` : ''}{val(ms, 'class') ? ` · ${val(ms, 'class')}` : ''}</span>
            {ms.members_names && <span>{ms.members_names}</span>}
            {val(ms, 'renewal_date') && <span>Renewal {dateVal(ms, 'renewal_date')}</span>}
            {val(ms, 'postcode') && <span>{val(ms, 'postcode')}</span>}
          </div>
        </div>
        <div className="mbr-chips">
          <StatusBadge status={ms.status} />
          {ms.has_dd ? <Badge kind="info">Direct debit</Badge> : null}
          {ms.gift_aid ? <Badge kind="ok">Gift Aid</Badge> : null}
          <SyncBadge status={ms.sync_status} discrepancies={ms.discrepancies} />
          <MasterBadge master={ms.master} />
          <button type="button" className="btn-secondary" onClick={reload}>Reload</button>
        </div>
      </div>

      <SubPills<MembershipTab> value={tab} onChange={setTab} tabs={[
        { key: 'details', label: 'Details' },
        { key: 'members', label: 'Members', n: data.members.length },
        { key: 'payments', label: 'Payments', n: data.payments?.n ?? 0 },
        { key: 'integrations', label: 'Integrations' },
      ]} />

      {tab === 'details' && <MembershipDetails ms={ms} />}
      {tab === 'members' && <PeopleTable rows={data.members} empty="No members are linked to this membership." />}
      {tab === 'payments' && <PaymentsPanel url={`/api/membership/memberships/${ms.membership_id}/payments`} giftAidApproved={(ms.gift_aid as boolean | null) ?? null} />}
      {tab === 'integrations' && <IntegrationsPanel entityType="membership" entityId={ms.membership_id} pairings={data.pairings} master={ms.master} diffs={[]} historyCount={data.history_count} onChanged={reload} />}
    </div>
  )
}

function MembershipDetails({ ms }: { ms: Record<string, unknown> }) {
  return (
    <div className="mbr-cols">
      <div className="mbr-section">
        <h3>Membership</h3>
        <Dl items={[['Membership number', val(ms, 'membership_number')], ['Old membership number', val(ms, 'old_membership_number') === '0' ? '' : val(ms, 'old_membership_number')], ['Category', `${val(ms, 'category')}${val(ms, 'category_short') ? ` (${val(ms, 'category_short')})` : ''}`], ['Type', val(ms, 'type')], ['Class', val(ms, 'class')], ['Source', val(ms, 'source')], ['Status', `${val(ms, 'status')} (code ${val(ms, 'status_code')})`], ['Status changed', fmtDateTime(ms.status_changed as string | null)], ['Members', val(ms, 'members_names')], ['Salutation', val(ms, 'salutation')], ['Moors Line', boolVal(ms, 'moorsline')], ['Cancellation reason', val(ms, 'cancellation_reason')], ['Gone away', boolVal(ms, 'gone_away')]]} />
        <h4>Dates</h4>
        <Dl items={[['Start date', dateVal(ms, 'start_date')], ['Duration', `${val(ms, 'duration')}${val(ms, 'duration_code') ? ` (code ${val(ms, 'duration_code')})` : ''}`], ['Renewal date', dateVal(ms, 'renewal_date')], ['Last renewed', dateVal(ms, 'last_renewed')], ['Renewed', boolVal(ms, 'renewed')], ['Renewal prepared', boolVal(ms, 'renewal_prepared')], ['Date of last donation', dateVal(ms, 'date_of_last_donation')]]} />
      </div>
      <div className="mbr-section">
        <h3>Money</h3>
        <Dl items={[['Payment type', val(ms, 'payment_type')], ['Payment method', `${val(ms, 'payment_method')}${val(ms, 'payment_method_code') ? ` (code ${val(ms, 'payment_method_code')})` : ''}`], ['Payment frequency', `${val(ms, 'payment_frequency')}${val(ms, 'payment_frequency_code') ? ` (code ${val(ms, 'payment_frequency_code')})` : ''}`], ['Fee paid', money(ms.fee_paid as number | null)], ['Donation paid', money(ms.donation_paid as number | null)], ['Gift Aid', boolVal(ms, 'gift_aid')]]} />
        <h4>GoCardless (as held in Ontrack)</h4>
        <Dl items={[['Mandate', val(ms, 'mandate_id')], ['Mandate status', val(ms, 'mandate_status')], ['Subscription', val(ms, 'subscription_id')], ['Subscription amount', money(ms.subscription_amount as number | null)], ['Subscription status', val(ms, 'subscription_status')], ['Next subscription date', dateVal(ms, 'next_subscription_date')], ['Next available charge', dateVal(ms, 'next_available_charge_date')], ['Direct debit inactive', boolVal(ms, 'dd_inactive')], ['DD cancelled on', dateVal(ms, 'dd_cancelled_on')], ['Payment processor', val(ms, 'payment_processor')], ['GoCardless comment', val(ms, 'gocardless_comment')], ['GoCardless info updated', fmtDateTime(ms.gocardless_info_updated as string | null)]]} />
      </div>
      <div className="mbr-section">
        <h3>Address</h3>
        <Dl items={[['Postal address', val(ms, 'postal_address')], ['Line 1', val(ms, 'address_line1')], ['Line 2', val(ms, 'address_line2')], ['Line 3', val(ms, 'address_line3')], ['Line 4', val(ms, 'address_line4')], ['Town', val(ms, 'town')], ['Region', val(ms, 'region')], ['County', val(ms, 'county')], ['Postcode', val(ms, 'postcode')], ['Country', val(ms, 'country')]]} />
        <h4>Analysis</h4>
        <Dl items={[['Analysis 1', val(ms, 'analysis1')], ['Analysis 2', val(ms, 'analysis2')], ['Analysis 3', val(ms, 'analysis3')]]} />
        <h4>Record</h4>
        <Dl items={[['Ontrack OID', val(ms, 'ontrack_oid')], ['Created in Ontrack', fmtDateTime(ms.ontrack_created_on as string | null)], ['Last seen in Ontrack', fmtDateTime(ms.ontrack_seen_at as string | null)], ['Deleted in Ontrack', fmtDateTime(ms.ontrack_deleted_at as string | null)], ['Sync master', val(ms, 'master') || 'none'], ['Sync status', val(ms, 'sync_status')], ['Created here', `${fmtDateTime(ms.created_at as string | null)} ${prettyUser(ms.created_by as string | null)}`.trim()]]} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
//  Payments (+ Gift Aid) for a member or a membership
// ---------------------------------------------------------------------------
export function PaymentsPanel({ url, giftAidApproved, declarations, paired }: { url: string; giftAidApproved: boolean | null; declarations?: DeclRow[]; paired?: boolean }) {
  const [view, setView] = useState<'payments' | 'giftaid'>('payments')
  const [ptype, setPtype] = useState('')
  const [source, setSource] = useState('')
  const fullUrl = useMemo(() => {
    const p = new URLSearchParams()
    if (ptype) p.set('f_payment_type', ptype)
    if (source) p.set('f_source', source)
    const qs = p.toString()
    return qs ? `${url}?${qs}` : url
  }, [url, ptype, source])
  const all = useJson<PaymentsPayload>(url)
  const filtered = useJson<PaymentsPayload>(fullUrl === url ? null : fullUrl)
  const data = fullUrl === url ? all.data : filtered.data
  const loading = fullUrl === url ? all.loading : filtered.loading
  const error = fullUrl === url ? all.error : filtered.error
  const types = useMemo(() => Array.from(new Set((all.data?.rows ?? []).map((r) => r.payment_type).filter((x): x is string => Boolean(x)))).sort(), [all.data])
  const rows = data?.rows ?? []
  const t = data?.totals

  return (
    <div className="mbr">
      <div className="mbr-toolbar">
        <SubPills<'payments' | 'giftaid'> value={view} onChange={setView} tabs={[{ key: 'payments', label: 'Payments', n: t?.n ?? null }, { key: 'giftaid', label: 'Gift Aid' }]} />
        <span className="mbr-spacer" />
        <label className="mbr-field">Payment type
          <select value={ptype} onChange={(e) => setPtype(e.target.value)}><option value="">Any</option>{types.map((x) => <option key={x} value={x}>{x}</option>)}</select>
        </label>
        <label className="mbr-field">Source
          <select value={source} onChange={(e) => setSource(e.target.value)}><option value="">Any</option><option value="ontrack">Ontrack</option><option value="gocardless">GoCardless only</option><option value="donorfy">Donorfy only</option></select>
        </label>
      </div>
      {error && <p className="mbr-error">{error}</p>}
      {view === 'payments' && (
        <>
          <div className="mbr-kpis">
            <KpiBox label="Payments" value={(t?.n ?? 0).toLocaleString('en-GB')} />
            <KpiBox label="Total" value={money(t?.total ?? 0)} />
            <KpiBox label="Collected" value={money(t?.collected ?? 0)} sub="Ontrack payments plus GoCardless confirmed or paid out" />
            <KpiBox label="Gift Aid estimate" value={money(t?.gift_aid_estimate ?? 0)} sub="25% of Gift Aided fee payments" />
          </div>
          <div className="mbr-table-wrap">
            <table className="hc-table">
              <thead><tr><th>Date</th><th>Source</th><th className="ta-right">Amount</th><th>Payment type</th><th>Method</th><th>Status</th><th>Reference</th><th>Narrative</th><th>Payer</th><th>Campaign</th><th>Gift Aid</th><th className="ta-right">GA est.</th><th>GoCardless</th></tr></thead>
              <tbody>
                {rows.map((r) => <PaymentTr key={`${r.source}-${r.source_key}`} r={r} />)}
                {!loading && rows.length === 0 && <tr><td colSpan={13} className="mbr-empty">No payments held for this record.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      {view === 'giftaid' && (
        <div className="mbr">
          <div className="mbr-kpis">
            <KpiBox label="Gift Aid approved" value={giftAidApproved === null ? 'Unknown' : giftAidApproved ? 'Yes' : 'No'} sub="The membership's Gift Aid flag in Ontrack" />
            <KpiBox label="Eligible payments" value={money(rows.filter((r) => r.gift_aid_estimate).reduce((a, r) => a + Number(r.amount ?? 0), 0))} sub="Gift Aided fee payments (Ontrack rule)" />
            <KpiBox label="Estimated claim" value={money(t?.gift_aid_estimate ?? 0)} sub="25% of the eligible amount" />
            <KpiBox label="Submitted" value="—" sub="From Donorfy — arrives with the Gift Aid build" />
            <KpiBox label="Confirmed" value="—" sub="From Donorfy — arrives with the Gift Aid build" />
          </div>
          <Placeholder title="Submitted and confirmed">
            Donorfy holds donations, not membership fees (checked 2026-09-22), so the fee claim history has to come from the membership side. That, the per-campaign split and the Donorfy figures for members' donations arrive in a later build. The estimate above follows Ontrack's own rule: 25% of every payment on a Gift Aided membership in a qualifying category.
          </Placeholder>
          {declarations !== undefined && (
            <div className="mbr-section">
              <h4>Donorfy Gift Aid declarations</h4>
              {!paired ? <p className="mbr-note">This member is not paired to a Donorfy constituent.</p>
                : declarations.length === 0 ? <p className="mbr-note">None held yet — on the Integrations tab press Sync now with Pull from Donorfy to read the constituent's declarations live.</p>
                : (
                  <div className="mbr-table-wrap">
                    <table className="hc-table">
                      <thead><tr><th>Declaration</th><th>Start</th><th>End</th><th>Method</th><th>Status</th><th>Active</th><th>Added</th><th>Pulled</th></tr></thead>
                      <tbody>
                        {declarations.map((d) => (
                          <tr key={d.declaration_id}><td>{d.declaration_id}</td><td>{fmtDate(d.start_date)}</td><td>{fmtDate(d.end_date)}</td><td>{d.method ?? ''}</td><td>{d.status ?? ''}</td><td>{d.is_active === null ? '' : d.is_active ? 'Yes' : 'No'}</td><td>{fmtDate(d.date_added)}</td><td>{fmtDateTime(d.refreshed_at)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          )}
          <div className="mbr-table-wrap">
            <table className="hc-table">
              <thead><tr><th>Date</th><th className="ta-right">Amount</th><th>Payment type</th><th>Gift Aid flag</th><th className="ta-right">Estimate</th><th>Status (Donorfy)</th><th className="ta-right">Claimed (Donorfy)</th><th>Campaign</th></tr></thead>
              <tbody>
                {rows.filter((r) => r.gift_aid || r.gift_aid_estimate || r.gift_aid_status).map((r) => (
                  <tr key={`${r.source}-${r.source_key}`}>
                    <td>{fmtDate(r.paid_on)}</td><td className="ta-right">{money(r.amount)}</td><td>{r.payment_type}</td><td>{r.gift_aid === null ? '' : r.gift_aid ? 'Yes' : 'No'}</td>
                    <td className="ta-right">{money(r.gift_aid_estimate)}</td><td>{r.gift_aid_status ?? ''}</td><td className="ta-right">{money(r.gift_aid_claimed)}</td><td>{r.campaign ?? ''}</td>
                  </tr>
                ))}
                {!loading && rows.filter((r) => r.gift_aid || r.gift_aid_estimate || r.gift_aid_status).length === 0 && <tr><td colSpan={8} className="mbr-empty">No Gift Aided payments.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function PaymentTr({ r }: { r: PaymentRow }) {
  return (
    <tr className={r.source === 'gocardless' ? 'mbr-row-gc' : undefined}>
      <td>{fmtDate(r.paid_on)}</td>
      <td>{r.source === 'ontrack' ? 'Ontrack' : r.source === 'gocardless' ? 'GoCardless' : 'Donorfy'}</td>
      <td className="ta-right">{money(r.amount)}</td>
      <td>{r.payment_type ?? ''}{r.is_donation ? ' (donation)' : ''}</td>
      <td>{r.payment_method ?? ''}</td>
      <td>{r.payment_status ?? ''}{r.is_collected === false ? <> <Badge kind="warn">not collected</Badge></> : null}</td>
      <td>{r.reference ?? ''}</td>
      <td className="wrap">{r.narrative ?? ''}</td>
      <td>{r.payer_name ?? r.member_name ?? ''}</td>
      <td>{r.campaign ?? ''}</td>
      <td>{r.gift_aid === null ? '' : r.gift_aid ? 'Yes' : 'No'}</td>
      <td className="ta-right">{money(r.gift_aid_estimate)}</td>
      <td>{r.gocardless_payment_id ? `${r.gocardless_payment_id}${r.gocardless_status ? ` · ${r.gocardless_status}` : ''}` : ''}</td>
    </tr>
  )
}

function KpiBox({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return <div className="mbr-kpi"><div className="mbr-kpi-label">{label}</div><div className="mbr-kpi-value">{value}</div>{sub && <div className="mbr-kpi-sub">{sub}</div>}</div>
}

// ---------------------------------------------------------------------------
//  Integrations for one record — live pairing boxes, the sync master, the differences against
//  Ontrack and Donorfy, and the record's history. Resolving a difference and the change batches
//  arrive in the next build (B2).
// ---------------------------------------------------------------------------
const BLURB: Record<SystemName, string> = {
  ontrack: 'The membership system of record. Paired automatically by the record\u2019s Ontrack OID.',
  donorfy: 'The fundraising CRM. Paired by Donorfy\u2019s ExternalKey = Ontrack person number on every Pull from Donorfy; otherwise find the person here.',
  gocardless: 'Direct debits. Paired from the GoCardless customer, mandate and subscription ids Ontrack holds; otherwise find the customer here.',
}

export function IntegrationsPanel({ entityType, entityId, pairings, master, diffs, historyCount, donorfy, onChanged }: {
  entityType: 'member' | 'membership'; entityId: number; pairings: Pairing[]; master: string | null; diffs: Diff[]; historyCount: number; donorfy?: DonorfyPaired | null; onChanged: () => void
}) {
  const [view, setView] = useState<'discrepancies' | 'history' | 'batches'>('discrepancies')
  const [tick, setTick] = useState(0)
  const changed = () => { setTick((t) => t + 1); onChanged() }
  const systems: SystemName[] = entityType === 'member' ? ['ontrack', 'donorfy', 'gocardless'] : ['ontrack', 'gocardless']
  const live = diffs.filter((d) => (d.differs_ontrack && !d.ignored_ontrack) || (d.differs_donorfy && !d.ignored_donorfy))
  return (
    <div className="mbr">
      <div className="mbr-pairings">
        {systems.map((sys) => (
          <PairingBox key={sys} system={sys} entityType={entityType} entityId={entityId} pairing={pairings.find((x) => x.system === sys)} blurb={BLURB[sys]} onChanged={onChanged}
            master={master} onQueued={() => { setView('batches'); changed() }}
            extra={sys === 'donorfy' && donorfy ? (
              <div className="mbr-note">
                {donorfy.display_name}{donorfy.email ? ` · ${donorfy.email}` : ''}{donorfy.postcode ? ` · ${donorfy.postcode}` : ''}
                {donorfy.is_active === false ? ' · inactive in Donorfy' : ''}{donorfy.gift_aid_refused ? ' · Gift Aid refused' : ''}
                {donorfy.tags && donorfy.tags.includes('TrustMembership') ? ' · Trust Membership tag' : ''}
                {donorfy.lifetime_gifts_amount ? ` · lifetime gifts ${money(donorfy.lifetime_gifts_amount)}` : ''}
              </div>
            ) : undefined} />
        ))}
      </div>
      <MasterSelector entityType={entityType} entityId={entityId} master={master} onChanged={onChanged} />
      <p><PendingBadges entityType={entityType} entityId={entityId} reloadKey={tick} /></p>
      <SubPills<'discrepancies' | 'history' | 'batches'> value={view} onChange={setView} tabs={[{ key: 'discrepancies', label: 'Discrepancies', n: live.length }, { key: 'batches', label: 'Change batches' }, { key: 'history', label: 'Historical changes', n: historyCount }]} />
      {view === 'discrepancies' && (
        diffs.length === 0 ? (
          <Placeholder title="No discrepancies">{entityType === 'member' ? 'every shared field agrees with Ontrack and, where paired, with Donorfy, as at the last pull.' : 'a membership has one source, Ontrack, so its fields mirror Ontrack on every pull.'}</Placeholder>
        ) : (
          <div className="mbr-table-wrap">
            <table className="hc-table">
              <thead><tr><th>Field</th><th>Membership (this system)</th><th>Ontrack</th><th>Donorfy</th><th>Resolve</th></tr></thead>
              <tbody>
                {diffs.map((d) => (
                  <tr key={d.field}>
                    <td>{fieldLabel(d.field)}</td>
                    <td className="mbr-diff-ours wrap">{d.ours ?? <span className="mbr-empty-val">(blank)</span>}</td>
                    <td className={'wrap' + (d.differs_ontrack ? ' mbr-diff-theirs' : '')}>{d.theirs ?? <span className="mbr-empty-val">(blank)</span>}{d.ignored_ontrack ? <> <Badge kind="muted">ignored</Badge></> : null}</td>
                    <td className={'wrap' + (d.differs_donorfy ? ' mbr-diff-theirs' : '')}>{d.donorfy === undefined || d.donorfy === null ? <span className="mbr-empty-val">{d.differs_donorfy ? '(blank)' : '—'}</span> : d.donorfy}{d.ignored_donorfy ? <> <Badge kind="muted">ignored</Badge></> : null}</td>
                    <td>{entityType === 'member' ? <ResolveCell row={{ member_id: entityId, field: d.field, membership_value: d.ours, ontrack_value: d.theirs, donorfy_value: d.donorfy ?? null, differs_ontrack: d.differs_ontrack ?? 0, differs_donorfy: d.differs_donorfy ?? 0, ignored_ontrack: d.ignored_ontrack, ignored_donorfy: d.ignored_donorfy }} scope="record" onDone={changed} /> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {view === 'batches' && <BatchesPanel key={`b-${tick}`} scope="record" entityType={entityType} entityId={entityId} title="This record's change batches" />}
      {view === 'history' && <HistoryTable key={`h-${tick}`} entityType={entityType} entityId={entityId} />}
    </div>
  )
}
